// /home/coder/project/frontend/src/pages/CorCountriesList.tsx
import React, { useState } from 'react'; // Keep useState
import { useNavigate } from 'react-router-dom';
import { useGetCountries } from '../../hooks/genericHooks';
import StandardTable from '../../components/common/StandardTable';
import { Card, Input, Space, Row, Col, Typography, message } from 'antd';
import {
  SearchOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { CorCountries } from '../../types';

const { Title } = Typography;

interface Filters {
  search: string;
}

const CorCountriesList: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>({ search: '' });

  // --- Data Fetching ---
  const { data: countriesData, isLoading, isError, error } = useGetCountries();
  const countryList = countriesData?.data || [];

  // --- Error Handling ---
  if (isError) {
    console.error("Failed to fetch countries:", error);
    message.error(`Could not load country data: ${error?.message || 'Unknown error'}`);
  }

  // --- Filtering ---
  const filteredCountries = countryList.filter((country) => {
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase();
      const searchFields = [
        country.country_code?.toLowerCase() || '',
        country.name?.toLowerCase() || '',
        country.currency_code?.toLowerCase() || '',
        country.unlocode_country_code?.toLowerCase() || '',
        country.unlocode_country_name?.toLowerCase() || '',
      ];
      if (!searchFields.some(field => field.includes(searchTerm))) {
        return false;
      }
    }
    return true;
  });

  // --- Table Columns ---
  const columns: ColumnsType<CorCountries> = [
    // ... (columns remain the same)
    {
      title: 'Country Code',
      dataIndex: 'country_code',
      key: 'country_code',
      sorter: (a, b) => (a.country_code || '').localeCompare(b.country_code || ''),
      width: 150,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      ellipsis: true,
    },
    {
      title: 'Currency Code',
      dataIndex: 'currency_code',
      key: 'currency_code',
      render: (code) => code || '-',
      sorter: (a, b) => (a.currency_code || '').localeCompare(b.currency_code || ''),
      width: 150,
    },
    {
      title: 'UNLOCODE Code',
      dataIndex: 'unlocode_country_code',
      key: 'unlocode_country_code',
      render: (v) => v || '-',
      sorter: (a, b) => (a.unlocode_country_code || '').localeCompare(b.unlocode_country_code || ''),
      width: 140,
    },
    {
      title: 'UNLOCODE Name',
      dataIndex: 'unlocode_country_name',
      key: 'unlocode_country_name',
      render: (v) => v || '-',
      sorter: (a, b) => (a.unlocode_country_name || '').localeCompare(b.unlocode_country_name || ''),
      ellipsis: true,
    },
  ];

  // --- Pagination Change Handler ---
  // highlight-start
  // This handler is called when the page number OR page size changes
  // const handlePaginationChange = (page: number, newPageSize?: number) => {
  //   console.log('Pagination Changed:', page, newPageSize);
  //   setCurrentPage(page);
  //   // If newPageSize is provided (meaning the size changer was used), update pageSize state
  //   if (newPageSize && newPageSize !== pageSize) {
  //     setPageSize(newPageSize);
  //     // Optional: Reset to page 1 when page size changes, common UX pattern
  //     // setCurrentPage(1);
  //   }
  // };
  // highlight-end

  // --- Render Component ---
  return (
    <Card>
      {/* Header Row */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <GlobalOutlined style={{ marginRight: 8 }} />
            Country List
          </Title>
        </Col>
      </Row>

      {/* Filter Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Search Code, Name, or Currency..."
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            allowClear
            disabled={isLoading}
          />
        </Col>
      </Row>

      {/* Data Table */}
      <StandardTable
        dataSource={filteredCountries}
        columns={columns}
        rowKey="country_code"
        pagination={{ pageSize: 20 }}
        loading={isLoading}
        bordered
        exportFileName="countries"
      />
    </Card>
  );
};

export default CorCountriesList;

